import { useMutation, useQuery } from "@tanstack/react-query";
import { Bug } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LabeledInput } from "@/components/ui/labeled-input";
import { errorDetail } from "@/lib/apiError";
import { cn } from "@/lib/utils";

interface Filed {
  number: number;
  url: string;
}

/** Whether "Report a problem" is set up on this server (ADR 0034). */
function useBugReportsEnabled(): boolean {
  const me = useQuery({
    queryKey: ["me", "bug-reports"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/auth/me", {});
      if (error || !data) throw new Error("could not load account");
      return data.bug_reports_enabled ?? false;
    },
  });
  return me.data === true;
}

/**
 * Sidebar entry and dialog that file a problem report in the
 * project's issue tracker through the API. Hidden when the server has
 * no tracker configured.
 */
export function ReportProblem({ className }: { className?: string }) {
  const { t } = useTranslation();
  const enabled = useBugReportsEnabled();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [includeContact, setIncludeContact] = useState(false);
  const descriptionId = useId();
  const hintId = useId();
  const contactId = useId();

  const send = useMutation({
    mutationFn: async (): Promise<Filed> => {
      const { data, error } = await api.POST("/api/v1/bug-reports", {
        body: {
          title: title.trim(),
          description: description.trim(),
          page: window.location.pathname,
          include_contact: includeContact,
        },
      });
      if (error || !data)
        throw new Error(errorDetail(error, t("errors.bug_report_unavailable")));
      return data;
    },
  });

  const reset = () => {
    setTitle("");
    setDescription("");
    setIncludeContact(false);
    send.reset();
  };

  if (!enabled) return null;
  const filed = send.data;
  const canSend =
    title.trim().length >= 3 &&
    description.trim().length > 0 &&
    !send.isPending;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          "flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-sm text-sidebar-foreground transition-colors hover:bg-white/5 hover:text-sidebar-active",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-ochre",
          className,
        )}
      >
        <Bug className="h-4 w-4 shrink-0" aria-hidden />
        {t("bug_report.button")}
      </button>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next && send.isSuccess) reset();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("bug_report.title")}</DialogTitle>
            <DialogDescription>{t("bug_report.intro")}</DialogDescription>
          </DialogHeader>
          {filed ? (
            <div className="space-y-3 text-sm" role="status">
              <p>{t("bug_report.sent", { number: filed.number })}</p>
              <a
                href={filed.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-4 hover:underline"
              >
                {t("bug_report.view")}
              </a>
              <DialogFooter>
                <Button onClick={() => setOpen(false)}>
                  {t("dialog.close")}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (canSend) send.mutate();
              }}
            >
              <div className="space-y-1">
                <LabeledInput
                  label={t("bug_report.summary")}
                  hideLabel={false}
                  value={title}
                  maxLength={120}
                  required
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor={descriptionId} className="text-sm">
                  {t("bug_report.description")}
                </label>
                <textarea
                  id={descriptionId}
                  aria-describedby={hintId}
                  value={description}
                  maxLength={8000}
                  required
                  rows={6}
                  onChange={(e) => setDescription(e.target.value)}
                  className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <p id={hintId} className="text-xs text-muted-foreground">
                  {t("bug_report.description_hint")}
                </p>
              </div>
              <div className="flex items-start gap-2 text-sm">
                <input
                  id={contactId}
                  type="checkbox"
                  className="mt-0.5"
                  checked={includeContact}
                  onChange={(e) => setIncludeContact(e.target.checked)}
                />
                <label htmlFor={contactId}>
                  {t("bug_report.include_contact")}
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("bug_report.attached")}
              </p>
              {send.isError && (
                <p role="alert" className="text-sm text-destructive">
                  {send.error.message}
                </p>
              )}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  {t("bug_report.cancel")}
                </Button>
                <Button type="submit" disabled={!canSend}>
                  {send.isPending
                    ? t("bug_report.sending")
                    : t("bug_report.send")}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
